/**
 * Reminder Store
 *
 * Manages reminder state using Zustand with persistence.
 * Provides CRUD operations for reminders including fetching,
 * creating, updating, and deleting reminders for meal slots.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Reminder, ReminderFormData } from '../types/reminder';
import api from '../services/api';

interface ReminderState {
  reminders: Reminder[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchReminders: (filters?: { meal_slot_id?: number; frequency?: string }) => Promise<void>;
  createReminder: (data: ReminderFormData) => Promise<Reminder>;
  updateReminder: (id: number, data: ReminderFormData) => Promise<Reminder>;
  deleteReminder: (id: number) => Promise<void>;

  // Utilities
  clearError: () => void;
}

/**
 * Reminder Store with Zustand
 *
 * Uses persist middleware to cache reminders for offline access.
 * Provides CRUD operations for reminders with error handling.
 */
export const useReminderStore = create<ReminderState>()(
  persist(
    (set) => ({
      reminders: [],
      isLoading: false,
      error: null,

      fetchReminders: async (filters = {}) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.get('/reminders', { params: filters });

          set({
            reminders: response.data.reminders || response.data,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch reminders',
          });
          throw error;
        }
      },

      createReminder: async (data: ReminderFormData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/reminders', { reminder: data });
          const newReminder = response.data.reminder || response.data;

          set((state) => ({
            reminders: [...state.reminders, newReminder],
            isLoading: false,
          }));

          return newReminder;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to create reminder',
          });
          throw error;
        }
      },

      updateReminder: async (id: number, data: ReminderFormData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.put(`/reminders/${id}`, { reminder: data });
          const updatedReminder = response.data.reminder || response.data;

          set((state) => ({
            reminders: state.reminders.map((reminder) =>
              reminder.id === id ? updatedReminder : reminder
            ),
            isLoading: false,
          }));

          return updatedReminder;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to update reminder',
          });
          throw error;
        }
      },

      deleteReminder: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          await api.delete(`/reminders/${id}`);

          set((state) => ({
            reminders: state.reminders.filter((reminder) => reminder.id !== id),
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to delete reminder',
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'reminder-storage', // localStorage key
      partialize: (state) => ({
        // Persist reminders for offline access
        // Don't persist loading/error states
        reminders: state.reminders,
      }),
    }
  )
);
